import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useBill } from '../api/restaurant';
import { formatDate, formatDateTime, formatGhs } from '../format';
import { api } from '../api/client';
import { useQuery } from '@tanstack/react-query';
import { Button, ErrorNote, Icon, Loading } from '../ui/kit';
import { ReceiptBrand } from '../ui/ReceiptBrand';

const TENDER_LABEL: Record<string, string> = { CASH: 'Cash', MOMO: 'Mobile money', CARD: 'Card', ROOM: 'Charged to room' };

/** The restaurant bill or receipt, print-ready like the room receipt (browser print, or save as PDF). */
export function BillReceipt() {
  const { id = '' } = useParams();
  const { me } = useAuth();
  const bill = useBill(id);
  const order = useQuery({
    queryKey: ['rs', 'order', bill.data?.order.id],
    enabled: !!bill.data,
    queryFn: () => api<{ lines: { id: string; itemName: string; variantName: string; qty: number; unitPrice: number; total: number; billId: string | null }[] }>(`/orders/${bill.data!.order.id}`),
  });

  if (bill.isLoading || order.isLoading) return <div className="page"><Loading /></div>;
  if (bill.error || !bill.data) return <div className="page"><ErrorNote error={bill.error} retry={() => void bill.refetch()} /></div>;

  const b = bill.data;
  const lines = (order.data?.lines ?? []).filter((l) => l.billId === b.id);
  const paid = b.status === 'PAID';
  const p = me?.property;
  const where = b.order.table ? `Table ${b.order.table}` : (b.order.label ?? 'Takeaway');

  return (
    <div className="page receipt-page">
      <div className="no-print row" style={{ justifyContent: 'space-between' }}>
        <Link to={`/restaurant/orders/${b.order.id}`} className="row small" style={{ textDecoration: 'none' }}>
          <span style={{ width: 18, height: 18 }}><Icon name="back" /></span>
          Back to the order
        </Link>
        <Button variant="primary" onClick={() => window.print()}>Print or save as PDF</Button>
      </div>

      <article className="receipt" aria-label={paid ? 'Receipt' : 'Bill'}>
        <header className="receipt-head">
          <ReceiptBrand property={p} subtitle="Restaurant and bar" showEmail={false} />
          <div className="receipt-meta">
            <div className="receipt-title">{paid ? 'Receipt' : 'Bill'}</div>
            <div className="num">{b.number}</div>
            <div>{formatDateTime(b.paidAt ?? b.createdAt)}</div>
            {paid ? <div className="stamp">Paid</div> : null}
          </div>
        </header>

        <section className="receipt-guest">
          <div>
            <div className="eyebrow">For</div>
            <strong>{where}</strong>
          </div>
          <div>
            <div className="eyebrow">Order</div>
            <div className="num">{b.order.reference}</div>
            {p ? <div>{formatDate(p.today, true)}</div> : null}
          </div>
        </section>

        <table className="receipt-table">
          <thead>
            <tr><th>Item</th><th className="r">Amount</th></tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.qty}× {l.itemName}
                  {l.variantName && l.variantName !== 'Regular' ? ` (${l.variantName})` : ''}
                  {l.qty > 1 ? <div className="receipt-sub">{formatGhs(l.unitPrice)} each</div> : null}
                </td>
                <td className="r num">{formatGhs(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <table className="receipt-totals">
          <tbody>
            {b.discount > 0 ? (
              <>
                <tr><td>Subtotal</td><td className="r num">{formatGhs(b.subtotal)}</td></tr>
                <tr><td>Discount</td><td className="r num">−{formatGhs(b.discount)}</td></tr>
              </>
            ) : null}
            <tr><td>Before tax</td><td className="r num">{formatGhs(b.breakdown.reduce((s, d) => s + d.net, 0))}</td></tr>
            {b.taxes.map((t) => (
              <tr key={t.code}><td>{t.name} ({t.rateBps / 100}%)</td><td className="r num">{formatGhs(t.amount)}</td></tr>
            ))}
            <tr className="strong"><td>Total</td><td className="r num">{formatGhs(b.total)}</td></tr>
            {b.payments.map((pm) => (
              <tr key={pm.id}>
                <td>{TENDER_LABEL[pm.tender] ?? pm.tender} <span className="receipt-sub">· {formatDateTime(pm.createdAt)}</span></td>
                <td className="r num">−{formatGhs(pm.amount)}</td>
              </tr>
            ))}
            {!paid ? <tr className="strong"><td>Balance due</td><td className="r num">{formatGhs(b.due)}</td></tr> : null}
          </tbody>
        </table>

        <footer className="receipt-foot">Thank you for dining with us.{b.taxes.length ? ' Prices include the taxes shown.' : ''}</footer>
      </article>
    </div>
  );
}
